import React, { useState, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { FileText, Upload, Link, Loader2, ChevronRight, AlertCircle, CheckCircle, X } from 'lucide-react';
import { geminiService } from '@/services/geminiService';
import { ocrService, OCRResult } from '@/services/ocrService';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import FileUploadSection from '@/components/questions/FileUploadSection';
import WebLinkSection from '@/components/questions/WebLinkSection';

interface Question {
  id: string;
  question_text: string;
  answer_text: string;
  question_type: string;
  difficulty: string;
  subject: string;
  source_type: string;
  source_data: any;
  options?: string[]; // For displaying multiple choice options
}

interface ChunkProgress {
  current: number;
  total: number;
  status: 'pending' | 'processing' | 'completed' | 'error';
  questions: Question[];
  error?: string;
}

const ResourceBasedQuizGenerator: React.FC = () => {
  const [activeTab, setActiveTab] = useState('file');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedQuestions, setGeneratedQuestions] = useState<Question[]>([]);
  const [extractedContent, setExtractedContent] = useState('');
  const [questionCount, setQuestionCount] = useState(10);
  const [chunkProgress, setChunkProgress] = useState<ChunkProgress[]>([]);
  const [currentChunk, setCurrentChunk] = useState(0);
  const [canCancel, setCanCancel] = useState(false);
  const [generationCancelled, setGenerationCancelled] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();
  const cancelRef = useRef(false);

  const QUESTIONS_PER_CHUNK = 15; // Maximum questions per API call
  const MAX_TOTAL_QUESTIONS = 100; // Maximum total questions allowed

  const resetGeneration = () => {
    setIsGenerating(false);
    setGeneratedQuestions([]);
    setExtractedContent('');
    setChunkProgress([]);
    setCurrentChunk(0);
    setCanCancel(false);
    setGenerationCancelled(false);
    cancelRef.current = false;
  };

  const handleCancel = () => {
    cancelRef.current = true;
    setGenerationCancelled(true);
    setCanCancel(false);
    toast({
      title: "Generation Cancelled",
      description: "Quiz generation has been cancelled.",
      variant: "destructive"
    });
  };

  const initializeChunks = (totalQuestions: number): ChunkProgress[] => {
    const numChunks = Math.ceil(totalQuestions / QUESTIONS_PER_CHUNK);
    return Array.from({ length: numChunks }, (_, index) => ({
      current: index + 1,
      total: numChunks,
      status: 'pending' as const,
      questions: []
    }));
  };

  const generateQuestionsChunk = async (
  content: string,
  questionsInChunk: number,
  chunkIndex: number,
  subject: string = '',
  sourceType: string = 'resource',
  sourceData: any = {})
  : Promise<Question[]> => {
    if (cancelRef.current) {
      throw new Error('Generation cancelled');
    }

    const prompt = `
Based on the following content, generate exactly ${questionsInChunk} quiz questions.

Content:
${content}

Requirements:
- Generate exactly ${questionsInChunk} questions
- Mix of question types: multiple choice, true/false, and short answer
- Vary difficulty levels: easy, medium, hard
- Each question should test understanding of the content
- For MULTIPLE CHOICE questions: Generate 4 options (A, B, C, D) with ONE correct answer
- For TRUE/FALSE questions: Answer should be "true" or "false"
- For SHORT ANSWER questions: Provide a brief correct answer
- Make questions specific and relevant to the content

IMPORTANT FORMAT for Multiple Choice Questions:
{
  "question_text": "Question here?",
  "question_type": "multiple_choice",
  "options": ["Option A", "Option B", "Option C", "Option D"],
  "correct_answer": "Option B",
  "difficulty": "easy|medium|hard",
  "subject": "${subject || 'General'}"
}

IMPORTANT FORMAT for True/False Questions:
{
  "question_text": "Statement here?",
  "question_type": "true_false", 
  "correct_answer": "true",
  "difficulty": "easy|medium|hard",
  "subject": "${subject || 'General'}"
}

IMPORTANT FORMAT for Short Answer Questions:
{
  "question_text": "Question here?",
  "question_type": "short_answer",
  "correct_answer": "Brief answer here",
  "difficulty": "easy|medium|hard", 
  "subject": "${subject || 'General'}"
}

Return only a JSON array of questions following the exact formats above, no additional text.
`;

    try {
      const response = await geminiService.generateContent(prompt);

      if (cancelRef.current) {
        throw new Error('Generation cancelled');
      }

      let questionsData;

      // Enhanced error handling for response processing
      try {
        // Check if response is already parsed or is a string
        if (typeof response === 'string') {
          const cleanedResponse = response.replace(/```json\n?|\n?```/g, '').trim();
          questionsData = JSON.parse(cleanedResponse);
        } else if (Array.isArray(response)) {
          // Response is already an array
          questionsData = response;
        } else if (typeof response === 'object' && response !== null) {
          // Response is an object, check if it has the questions data
          if (Array.isArray(response.questions)) {
            questionsData = response.questions;
          } else if (response.text && typeof response.text === 'string') {
            // Try to parse the text field
            const cleanedText = response.text.replace(/```json\n?|\n?```/g, '').trim();
            questionsData = JSON.parse(cleanedText);
          } else {
            questionsData = response;
          }
        } else {
          throw new Error('Invalid response format from AI service');
        }
      } catch (parseError) {
        console.error('Failed to parse questions JSON:', parseError);
        console.error('Raw response:', response);
        throw new Error('Failed to parse generated questions');
      }

      if (!Array.isArray(questionsData)) {
        console.error('Questions data is not an array:', questionsData);
        throw new Error('Generated content is not a valid questions array');
      }

      return questionsData.map((q, index) => {
        let answerText = '';
        let options: string[] = [];

        // Process the question based on its type
        if (q.question_type === 'multiple_choice') {
          if (q.options && Array.isArray(q.options) && q.options.length >= 4) {
            // Use the provided options
            options = q.options.slice(0, 4); // Ensure we only take 4 options
            answerText = JSON.stringify({
              correct_answer: q.correct_answer || q.options[0],
              options: options
            });
          } else {
            // Fallback: try to extract from answer_text if it's a JSON string
            try {
              const parsedAnswer = typeof q.answer_text === 'string' ? JSON.parse(q.answer_text) : q.answer_text;
              if (parsedAnswer && parsedAnswer.options && Array.isArray(parsedAnswer.options)) {
                options = parsedAnswer.options.slice(0, 4);
                answerText = JSON.stringify({
                  correct_answer: parsedAnswer.correct_answer || q.correct_answer || options[0],
                  options: options
                });
              } else {
                // Generate default options if none provided
                const correctAnswer = q.correct_answer || q.answer_text || "Option A";
                options = [
                correctAnswer,
                "Option B (Generated)",
                "Option C (Generated)",
                "Option D (Generated)"];

                answerText = JSON.stringify({
                  correct_answer: correctAnswer,
                  options: options
                });
              }
            } catch {
              // If parsing fails, create default structure
              const correctAnswer = q.correct_answer || q.answer_text || "Option A";
              options = [
              correctAnswer,
              "Option B (Generated)",
              "Option C (Generated)",
              "Option D (Generated)"];

              answerText = JSON.stringify({
                correct_answer: correctAnswer,
                options: options
              });
            }
          }
        } else {
          // For true/false and short answer questions
          answerText = q.correct_answer || q.answer_text || '';
        }

        return {
          id: `chunk-${chunkIndex}-q-${index}`,
          question_text: q.question_text || '',
          answer_text: answerText,
          question_type: q.question_type || 'multiple_choice',
          difficulty: q.difficulty || 'medium',
          subject: q.subject || subject || 'General',
          source_type: sourceType,
          source_data: sourceData,
          options: options.length > 0 ? options : undefined // For display purposes
        };
      });
    } catch (error) {
      console.error(`Error generating questions for chunk ${chunkIndex + 1}:`, error);
      throw error;
    }
  };

  const generateQuestionsInChunks = async (
  content: string,
  totalQuestions: number,
  subject: string = '',
  sourceType: string = 'resource',
  sourceData: any = {}) =>
  {
    const chunks = initializeChunks(totalQuestions);
    setChunkProgress(chunks);
    setCanCancel(true);

    const allQuestions: Question[] = [];
    let questionsGenerated = 0;

    for (let i = 0; i < chunks.length; i++) {
      if (cancelRef.current) {
        setChunkProgress((prev) => prev.map((chunk, index) =>
        index === i ? { ...chunk, status: 'error', error: 'Cancelled' } : chunk
        ));
        break;
      }

      setCurrentChunk(i);

      // Update chunk status to processing
      setChunkProgress((prev) => prev.map((chunk, index) =>
      index === i ? { ...chunk, status: 'processing' } : chunk
      ));

      try {
        const questionsRemaining = totalQuestions - questionsGenerated;
        const questionsInThisChunk = Math.min(QUESTIONS_PER_CHUNK, questionsRemaining);

        const chunkQuestions = await generateQuestionsChunk(
          content,
          questionsInThisChunk,
          i,
          subject,
          sourceType,
          sourceData
        );

        if (cancelRef.current) break;

        allQuestions.push(...chunkQuestions);
        questionsGenerated += chunkQuestions.length;

        // Update chunk status to completed
        setChunkProgress((prev) => prev.map((chunk, index) =>
        index === i ? {
          ...chunk,
          status: 'completed',
          questions: chunkQuestions
        } : chunk
        ));

        // Small delay between chunks to prevent overwhelming the API
        if (i < chunks.length - 1 && !cancelRef.current) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }

      } catch (error) {
        console.error(`Error in chunk ${i + 1}:`, error);

        // Update chunk status to error
        setChunkProgress((prev) => prev.map((chunk, index) =>
        index === i ? {
          ...chunk,
          status: 'error',
          error: error instanceof Error ? error.message : 'Unknown error'
        } : chunk
        ));

        // Continue with next chunk instead of stopping completely
        if (!cancelRef.current) {
          toast({
            title: "Chunk Error",
            description: `Error in chunk ${i + 1}, continuing with remaining chunks...`,
            variant: "destructive"
          });
        }
      }
    }

    setCanCancel(false);
    return allQuestions;
  };

  const handleFileContent = async (content: string, filename: string) => {
    setExtractedContent(content);

    if (questionCount > MAX_TOTAL_QUESTIONS) {
      toast({
        title: "Too Many Questions",
        description: `Maximum ${MAX_TOTAL_QUESTIONS} questions allowed. Please reduce the number.`,
        variant: "destructive"
      });
      return;
    }

    setIsGenerating(true);
    resetGeneration();

    try {
      const questions = await generateQuestionsInChunks(
        content,
        questionCount,
        'Document Content',
        'file',
        { filename }
      );

      if (!cancelRef.current) {
        setGeneratedQuestions(questions);
        toast({
          title: "Questions Generated",
          description: `Successfully generated ${questions.length} questions from ${filename}.`
        });
      }
    } catch (error) {
      if (!cancelRef.current) {
        console.error('Error generating questions from file:', error);
        toast({
          title: "Generation Failed",
          description: "Failed to generate questions from the file content.",
          variant: "destructive"
        });
      }
    } finally {
      setIsGenerating(false);
    }
  };

  // Enhanced handler for web link questions extraction with proper error handling
  const handleWebQuestionsExtracted = async (result: OCRResult) => {
    try {
      console.log('Processing web link result:', result);

      setExtractedContent(result.extractedText);

      if (questionCount > MAX_TOTAL_QUESTIONS) {
        toast({
          title: "Too Many Questions",
          description: `Maximum ${MAX_TOTAL_QUESTIONS} questions allowed. Please reduce the number.`,
          variant: "destructive"
        });
        return;
      }

      setIsGenerating(true);
      resetGeneration();

      // Check if we have questions from the OCR result and use them directly if they meet our needs
      if (result.questions && Array.isArray(result.questions) && result.questions.length >= questionCount) {
        console.log('Using questions directly from OCR result');

        const limitedQuestions = result.questions.slice(0, questionCount).map((q, index) => ({
          id: `web-q-${index}`,
          question_text: q.question_text || '',
          answer_text: q.answer_text || '',
          question_type: q.question_type || 'multiple_choice',
          difficulty: q.difficulty || 'medium',
          subject: q.subject || result.subject || 'Web Content',
          source_type: 'weblink',
          source_data: { url: result.fileName }
        }));

        setGeneratedQuestions(limitedQuestions);
        toast({
          title: "Questions Generated",
          description: `Successfully generated ${limitedQuestions.length} questions from web content.`
        });
      } else if (result.questions && Array.isArray(result.questions) && result.questions.length < questionCount) {
        console.log('Using OCR questions and generating additional ones');

        // Use existing questions and generate more to reach the target count
        const ocrQuestions = result.questions.map((q, index) => ({
          id: `web-ocr-q-${index}`,
          question_text: q.question_text || '',
          answer_text: q.answer_text || '',
          question_type: q.question_type || 'multiple_choice',
          difficulty: q.difficulty || 'medium',
          subject: q.subject || result.subject || 'Web Content',
          source_type: 'weblink',
          source_data: { url: result.fileName }
        }));

        const remainingCount = questionCount - ocrQuestions.length;

        const additionalQuestions = await generateQuestionsInChunks(
          result.extractedText,
          remainingCount,
          result.subject || 'Web Content',
          'weblink',
          { url: result.fileName }
        );

        if (!cancelRef.current) {
          const allQuestions = [...ocrQuestions, ...additionalQuestions];
          setGeneratedQuestions(allQuestions);
          toast({
            title: "Questions Generated",
            description: `Successfully generated ${allQuestions.length} questions from web content.`
          });
        }
      } else {
        console.log('Generating questions using chunked approach');

        // Generate questions using our chunked approach
        const questions = await generateQuestionsInChunks(
          result.extractedText,
          questionCount,
          result.subject || 'Web Content',
          'weblink',
          { url: result.fileName }
        );

        if (!cancelRef.current) {
          setGeneratedQuestions(questions);
          toast({
            title: "Questions Generated",
            description: `Successfully generated ${questions.length} questions from web content.`
          });
        }
      }
    } catch (error) {
      if (!cancelRef.current) {
        console.error('Error processing web link result:', error);
        toast({
          title: "Generation Failed",
          description: error instanceof Error ? error.message : "Failed to generate questions from the web content.",
          variant: "destructive"
        });
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const saveQuestionsToPool = async () => {
    if (!user?.ID || generatedQuestions.length === 0) return;

    try {
      const questionsData = generatedQuestions.map((q) => ({
        teacher_id: user.ID,
        question_text: q.question_text,
        answer_text: q.answer_text,
        question_type: q.question_type,
        subject: q.subject,
        difficulty: q.difficulty,
        tags: JSON.stringify([]),
        source_type: q.source_type,
        source_data: JSON.stringify(q.source_data),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }));

      for (const questionData of questionsData) {
        const { error } = await window.ezsite.apis.tableCreate(16057, questionData);
        if (error) throw new Error(error);
      }

      toast({
        title: "Questions Saved",
        description: `Successfully saved ${generatedQuestions.length} questions to your question pool.`
      });

      resetGeneration();
    } catch (error) {
      console.error('Error saving questions:', error);
      toast({
        title: "Save Failed",
        description: "Failed to save questions to the question pool.",
        variant: "destructive"
      });
    }
  };

  const renderQuestionPreview = (question: Question, index: number) => {
    let answerDisplay = question.answer_text;

    // For multiple choice questions, parse and display options
    if (question.question_type === 'multiple_choice') {
      try {
        const parsedAnswer = JSON.parse(question.answer_text);
        if (parsedAnswer.options && Array.isArray(parsedAnswer.options)) {
          return (
            <div key={question.id} className="p-4 border rounded-lg space-y-3" data-id="hudehpz0a" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">
              <div className="flex items-start justify-between" data-id="tsp04rktj" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">
                <h4 className="font-medium" data-id="6o3mv45ag" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">Q{index + 1}: {question.question_text}</h4>
                <div className="flex gap-1" data-id="aiakpds56" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">
                  <Badge variant="outline" data-id="5r4lfaf0d" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">{question.question_type}</Badge>
                  <Badge variant="outline" data-id="6vp6j82zw" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">{question.difficulty}</Badge>
                </div>
              </div>
              
              <div className="space-y-2" data-id="ezo0c3h58" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">
                <p className="text-sm font-medium text-gray-700" data-id="k6rfhui55" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">Options:</p>
                <div className="grid grid-cols-1 gap-1 ml-4" data-id="y0kasnudl" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">
                  {parsedAnswer.options.map((option: string, optionIndex: number) =>
                  <div key={optionIndex} className={`text-sm p-2 rounded ${option === parsedAnswer.correct_answer ? 'bg-green-100 text-green-800 font-medium' : 'bg-gray-50'}`} data-id="mwxut7ckd" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">
                      {String.fromCharCode(65 + optionIndex)}. {option}
                      {option === parsedAnswer.correct_answer && ' ✓'}
                    </div>
                  )}
                </div>
                <p className="text-sm text-green-600 font-medium" data-id="4uc6bnmmp" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">
                  Correct Answer: {parsedAnswer.correct_answer}
                </p>
              </div>
            </div>);

        }
      } catch (error) {
































        // Fall back to regular display if parsing fails
      }} // For other question types or if parsing fails
    return <div key={question.id} className="p-4 border rounded-lg space-y-2" data-id="yw9rf6bl1" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">
        <div className="flex items-start justify-between" data-id="1c26m766q" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">
          <h4 className="font-medium" data-id="paaqgl4fg" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">Q{index + 1}: {question.question_text}</h4>
          <div className="flex gap-1" data-id="hdng2zrwz" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">
            <Badge variant="outline" data-id="epeprzl5e" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">{question.question_type}</Badge>
            <Badge variant="outline" data-id="acm9jcz1v" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">{question.difficulty}</Badge>
          </div>
        </div>
        <p className="text-sm text-gray-600" data-id="laoq13bzl" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">
          <strong data-id="jluumyui1" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">Answer:</strong> {answerDisplay}
        </p>
      </div>;};const renderChunkProgress = () => {if (chunkProgress.length === 0) return null;const completedChunks = chunkProgress.filter((c) => c.status === 'completed').length;const totalProgress = completedChunks / chunkProgress.length * 100;return <div className="space-y-4" data-id="thteg4ysf" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">
        <div className="flex items-center justify-between" data-id="qjqoew1g3" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">
          <h4 className="font-medium" data-id="paobqfuuz" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">Generation Progress</h4>
          {canCancel && <Button variant="outline" size="sm" onClick={handleCancel} className="text-red-600 hover:text-red-700" data-id="th75cw2xd" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">
              <X className="h-4 w-4 mr-1" data-id="gyq6jdqrq" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx" />
              Cancel
            </Button>}
        </div>
        
        <div className="space-y-2" data-id="o54g8377j" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">
          <div className="flex justify-between text-sm" data-id="iik6sktny" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">
            <span data-id="v4m2mgcnr" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">Overall Progress</span>
            <span data-id="58fvy2ymi" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">{completedChunks}/{chunkProgress.length} chunks</span>
          </div>
          <Progress value={totalProgress} className="h-2" data-id="90v8t95t3" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx" />
        </div>

        <div className="space-y-2 max-h-32 overflow-y-auto" data-id="9hn3m841g" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">
          {chunkProgress.map((chunk, index) => <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded" data-id="jfgxxys2a" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">
              <span className="text-sm" data-id="k0trpin24" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">Chunk {chunk.current}</span>
              <div className="flex items-center gap-2" data-id="i60r3scvg" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">
                {chunk.status === 'pending' && <Badge variant="secondary" data-id="65ox2yqqy" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">Pending</Badge>}
                {chunk.status === 'processing' && <Badge variant="default" className="bg-blue-500" data-id="g4vkgbd7t" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" data-id="d9b1p5gs0" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx" />
                    Processing
                  </Badge>}
                {chunk.status === 'completed' && <Badge variant="default" className="bg-green-500" data-id="dhpe24k9h" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">
                    <CheckCircle className="h-3 w-3 mr-1" data-id="shkduw0pl" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx" />
                    {chunk.questions.length} questions
                  </Badge>}
                {chunk.status === 'error' && <Badge variant="destructive" data-id="ktj7eyi0f" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">
                    <AlertCircle className="h-3 w-3 mr-1" data-id="zua35x47b" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx" />
                    Error
                  </Badge>}
              </div>
            </div>)}
        </div>
      </div>;
  };

  return (
    <Card className="w-full" data-id="rlmnyb2fg" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">
      <CardHeader data-id="vse75v9yp" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">
        <CardTitle className="flex items-center gap-2" data-id="zlh6xyvti" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">
          <FileText className="h-5 w-5" data-id="ayg78rjyj" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx" />
          Resource-Based Quiz Generator
        </CardTitle>
        <CardDescription data-id="8ook600bl" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">
          Generate quiz questions from files or web content with customizable question count
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6" data-id="e89jt4puh" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">
        {/* Question Count Input */}
        <div className="space-y-2" data-id="zovs2u1ih" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">
          <Label htmlFor="questionCount" data-id="sg5owlmz6" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">Number of Questions</Label>
          <div className="flex items-center gap-4" data-id="5sscex3e2" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">
            <Input
              id="questionCount"
              type="number"
              min="1"
              max={MAX_TOTAL_QUESTIONS}
              value={questionCount}
              onChange={(e) => setQuestionCount(Math.min(MAX_TOTAL_QUESTIONS, Math.max(1, parseInt(e.target.value) || 1)))}
              className="w-32"
              disabled={isGenerating} data-id="xdk8kgwka" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx" />

            <span className="text-sm text-gray-500" data-id="x5hdzh2ja" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">
              (Max: {MAX_TOTAL_QUESTIONS}, will be generated in chunks of {QUESTIONS_PER_CHUNK})
            </span>
          </div>
          {questionCount > QUESTIONS_PER_CHUNK &&
          <Alert data-id="mcmy2vwab" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">
              <AlertCircle className="h-4 w-4" data-id="e1l2n9dug" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx" />
              <AlertDescription data-id="ukjmxda2x" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">
                Large question sets will be generated in chunks for better performance. 
                This may take several minutes to complete.
              </AlertDescription>
            </Alert>
          }
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} data-id="k4z6ymzd1" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">
          <TabsList className="grid w-full grid-cols-2" data-id="42ixu887f" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">
            <TabsTrigger value="file" className="flex items-center gap-2" data-id="9ajlkrsqz" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">
              <Upload className="h-4 w-4" data-id="5szusecqr" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx" />
              File Upload
            </TabsTrigger>
            <TabsTrigger value="weblink" className="flex items-center gap-2" data-id="tlqbr6cqa" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">
              <Link className="h-4 w-4" data-id="b0uhf9nxq" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx" />
              Web Link
            </TabsTrigger>
          </TabsList>

          <TabsContent value="file" className="space-y-4" data-id="rotocsdh5" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">
            <FileUploadSection
              onContentExtracted={handleFileContent}
              disabled={isGenerating} data-id="5ai806mzn" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx" />

          </TabsContent>

          <TabsContent value="weblink" className="space-y-4" data-id="noyd1md7b" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">
            <WebLinkSection
              onQuestionsExtracted={handleWebQuestionsExtracted}
              disabled={isGenerating} data-id="w3xggrus6" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx" />

          </TabsContent>
        </Tabs>

        {/* Generation Progress */}
        {(isGenerating || chunkProgress.length > 0) &&
        <Card data-id="tn0xpn1d8" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">
            <CardContent className="pt-6" data-id="urwhfq8p9" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">
              {renderChunkProgress()}
            </CardContent>
          </Card>
        }

        {/* Generated Questions */}
        {generatedQuestions.length > 0 && !isGenerating &&
        <Card data-id="hd5dw08if" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">
            <CardHeader data-id="tikkvn0oy" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">
              <CardTitle className="flex items-center justify-between" data-id="l8w7468s2" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">
                <span data-id="xz17aak4w" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">Generated Questions ({generatedQuestions.length})</span>
                <Button onClick={saveQuestionsToPool} data-id="335hyr2p8" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">
                  Save to Question Pool
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 max-h-96 overflow-y-auto" data-id="iln4pkxxl" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">
              {generatedQuestions.map((question, index) =>
            renderQuestionPreview(question, index)
            )}
            </CardContent>
          </Card>
        }

        {generationCancelled &&
        <Alert variant="destructive" data-id="g868ohalw" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">
            <AlertCircle className="h-4 w-4" data-id="8kmnw3shg" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx" />
            <AlertDescription data-id="yoy7yyo5m" data-path="src/components/ai/ResourceBasedQuizGenerator.tsx">
              Question generation was cancelled. You can restart the process at any time.
            </AlertDescription>
          </Alert>
        }
      </CardContent>
    </Card>);

};

export default ResourceBasedQuizGenerator;